import { centerOpticalCount } from '@theme/optical-count';
import { normalizeWheelDeltas } from './monolyt-wheel.js?v=ad023c9c05a6ab2c36d4800155ae48ae62083b8128ae71e59d59a38e5a98f62d';
import { hasConstrainedMediaConnection } from './monolyt-media-policy.js?v=2600083df17ccd19bc7a6adc6f5ae417cc3913aaa3119ca87d4e893b97c56623';
import { attachMobileSheet } from './monolyt-mobile-sheet.js?v=9f4d1c9abba3d75180c7ca0044fe722ae8d666c2fcd108d518eed0cc8e0588f8';
import { PageifyMediaController } from './monolyt-pageify-media.js?v=8e2cf1bd6e5cbfac2ab5af229b629a2da8c8add742b649a98dcedce9bc5cff0c';

const ROOT_SELECTOR = '[data-pageify-root]';
const BACKGROUND_HOST_SELECTOR = '[data-pageify-background-host]';
const BACKGROUND_LAYER_SELECTOR = '[data-pageify-background-layer]';
const instances = new WeakMap();
const liveInstances = new Set();
let generatedId = 0;

class MonolytPageify {
  constructor(root) {
    this.root = root;
    this.index = 0;
    this.sequence = 0;
    this.turnLocked = false;
    this.wheelDeltaTotal = 0;
    this.wheelIgnoreUntil = 0;
    this.wheelLastInputAt = 0;
    this.wheelLastTurnAt = 0;
    this.wheelGestureBlockedUntilQuiet = false;
    this.wheelGestureUsedNativeScroll = false;
    this.readingWheel = null;
    this.touch = null;
    this.timers = new Set();
    this.leavingPanels = new WeakMap();
    this.backgroundLayerPortals = new Map();
    this.abortController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;
    this.media = new PageifyMediaController({
      getBackgrounds: () => this.backgrounds,
      getActiveBackground: () => this.backgrounds[this.index],
      isConnected: () => this.connected,
      allowsPlayback: () => this.allowsVideoPlayback(),
    });

    if (!this.buildRegistry()) return;
    this.index = this.resolveInitialIndex();

    this.navigation = this.findNavigation();
    if (!this.navigation) return;

    this.previousButton = this.navigation.querySelector('[data-pageify-prev]');
    this.nextButton = this.navigation.querySelector('[data-pageify-next]');
    this.currentCounter = this.navigation.querySelector('[data-pageify-current]');
    this.totalCounter = this.navigation.querySelector('[data-pageify-total]');
    this.statusCounter = this.navigation.querySelector('[data-pageify-status]');
    if (!this.previousButton || !this.nextButton || !this.currentCounter || !this.totalCounter) return;
    if (!this.portalBackgroundLayers()) return;

    this.connected = true;
    this.bindEvents();
    this.commitState(this.index, 0);
    this.root.classList.add('is-enhanced');
    this.navigation.removeAttribute('data-pageify-unready');
    this.navigation.removeAttribute('aria-hidden');
    this.previousButton.removeAttribute('tabindex');
    this.nextButton.removeAttribute('tabindex');
    document.body.classList.add('pageify-is-enhanced');
    document.documentElement.classList.remove('monolyt-pageify-pending');
    this.setupMobileProductSheet();
  }

  setupMobileProductSheet() {
    this.mobileProductSheetCleanup?.();
    this.mobileProductSheetCleanup = attachMobileSheet(this.root);
  }

  ownedElements(selector) {
    return [...this.root.querySelectorAll(selector)].filter((element) => element.closest(ROOT_SELECTOR) === this.root);
  }

  findBackgroundHost() {
    return [...this.root.children].find((element) => element.matches(BACKGROUND_HOST_SELECTOR)) || null;
  }

  pruneBackgroundLayerPortals() {
    this.backgroundLayerPortals.forEach((portal, layer) => {
      if (portal.anchor.isConnected && this.root.contains(portal.anchor)) return;
      layer.remove();
      portal.anchor.remove();
      this.backgroundLayerPortals.delete(layer);
    });
  }

  sortBackgroundHostLayers() {
    const portals = [...this.backgroundLayerPortals.values()].filter((portal) => portal.anchor.isConnected);
    portals.sort((first, second) => {
      const position = first.anchor.compareDocumentPosition(second.anchor);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    portals.forEach(({ layer }) => this.backgroundHost.append(layer));
  }

  portalBackgroundLayers() {
    this.backgroundHost = this.findBackgroundHost();
    if (!this.backgroundHost) return false;

    this.pruneBackgroundLayerPortals();
    const layers = this.ownedElements(BACKGROUND_LAYER_SELECTOR)
      .filter((layer) => !this.backgroundLayerPortals.has(layer));
    layers.forEach((layer) => {
      // The anchor preserves provider ownership and exact source order while the visual layer shares one root host.
      const anchor = document.createComment(' monolyt-pageify-background-layer ');
      layer.before(anchor);
      this.backgroundLayerPortals.set(layer, { anchor, layer });
      this.backgroundHost.append(layer);
    });

    if (!this.backgroundLayerPortals.size) return false;
    this.sortBackgroundHostLayers();
    this.root.setAttribute('data-pageify-backgrounds-hosted', '');
    return true;
  }

  restoreBackgroundLayers(scope = null) {
    this.backgroundLayerPortals.forEach((portal, layer) => {
      const { anchor } = portal;
      if (scope && scope !== anchor.parentNode && !scope.contains?.(anchor)) return;

      if (anchor.parentNode) {
        anchor.replaceWith(layer);
      } else {
        layer.remove();
      }
      this.backgroundLayerPortals.delete(layer);
    });

    if (!this.backgroundLayerPortals.size) {
      this.root.removeAttribute('data-pageify-backgrounds-hosted');
    }
  }

  buildRegistry() {
    const panels = this.ownedElements('[data-pageify-panel]');
    const backgrounds = this.ownedElements('[data-pageify-background]');
    if (!panels.length || panels.length !== backgrounds.length) return false;

    const panelKeys = panels.map((panel, index) => panel.dataset.pageifyKey || String(index));
    const backgroundKeys = backgrounds.map((background, index) => background.dataset.pageifyKey || String(index));
    if (new Set(panelKeys).size !== panelKeys.length || new Set(backgroundKeys).size !== backgroundKeys.length) return false;

    const backgroundsByKey = new Map(backgrounds.map((background, index) => [backgroundKeys[index], background]));
    if (!panelKeys.every((key) => backgroundsByKey.has(key))) return false;

    this.panels = panels;
    this.panelKeys = panelKeys;
    this.backgrounds = panelKeys.map((key) => backgroundsByKey.get(key));
    this.stageSynchronizedBackgrounds = Boolean(this.findBackgroundHost());
    return true;
  }

  resolveInitialIndex() {
    let hashId = window.location.hash.slice(1);
    try {
      hashId = decodeURIComponent(hashId);
    } catch {
      // Keep the browser-provided hash when it is not valid percent-encoding.
    }
    const hashTarget = hashId ? document.getElementById(hashId) : null;
    const autofocusTarget = this.root.querySelector('[autofocus]');
    const focusedTarget = document.activeElement instanceof Element ? document.activeElement : null;

    for (const target of [hashTarget, autofocusTarget, focusedTarget]) {
      if (!(target instanceof Element)) continue;
      const index = this.panels.findIndex((panel) => panel.contains(target));
      if (index >= 0) return index;
    }

    return 0;
  }

  findNavigation() {
    if (!this.root.id) this.root.id = `MonolytPageify-${++generatedId}`;
    const navigation = document.querySelector(`[data-pageify-navigation][aria-controls="${CSS.escape(this.root.id)}"]`)
      || document.querySelector('[data-pageify-navigation]:not([aria-controls])');
    this.navigationHadAriaControls = navigation?.hasAttribute('aria-controls') || false;
    navigation?.setAttribute('aria-controls', this.root.id);
    return navigation;
  }

  bindEvents() {
    const signal = this.abortController.signal;
    this.previousButton.addEventListener('click', () => this.turn(-1, 'button'), { signal });
    this.nextButton.addEventListener('click', () => this.turn(1, 'button'), { signal });
    this.root.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.navigation.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.root.addEventListener('wheel', (event) => this.onWheel(event), { capture: true, passive: false, signal });
    this.root.addEventListener('pointerdown', () => { this.readingWheel = null; }, { capture: true, signal });
    this.root.addEventListener('pointerout', (event) => {
      if (this.readingWheel && (!(event.relatedTarget instanceof Node) || !this.readingWheel.owner.contains(event.relatedTarget))) {
        this.readingWheel = null;
      }
    }, { signal });
    this.root.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
    this.root.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
    this.root.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true, signal });
    this.root.addEventListener('touchcancel', () => this.resetTouch(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.media.pause();
      } else {
        this.media.sync();
      }
    }, { signal });
    this.onMotionPreferenceChange = () => this.media.sync();
    this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
    this.onConnectionChange = () => this.media.sync();
    this.connection?.addEventListener?.('change', this.onConnectionChange);

    if (this.stageSynchronizedBackgrounds && typeof ResizeObserver === 'function') {
      this.backgroundResizeObserver = new ResizeObserver(() => this.syncOffsets());
      this.backgroundResizeObserver.observe(this.root);
      this.backgroundResizeObserver.observe(this.backgroundHost);
    } else if (this.stageSynchronizedBackgrounds) {
      window.addEventListener('resize', () => this.syncOffsets(), { signal });
    }
  }

  schedule(callback, delay) {
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delay);
    this.timers.add(timer);
    return timer;
  }

  eventDetail(fromIndex, toIndex, direction, source) {
    return {
      fromIndex,
      toIndex,
      direction,
      source,
      fromPanel: this.panels[fromIndex],
      toPanel: this.panels[toIndex],
    };
  }

  /** Read only the existing transform transition; input locks stay independent. */
  transformTransitionDuration(panel) {
    if (this.reducedMotion.matches || !panel) return 0;

    const style = window.getComputedStyle(panel);
    const properties = style.transitionProperty.split(',').map((property) => property.trim());
    const durations = style.transitionDuration.split(',').map((duration) => duration.trim());
    let milliseconds = 0;

    properties.forEach((property, index) => {
      if (property !== 'transform' && property !== 'all') return;
      // CSS repeats shorter duration lists; the last matching property wins.
      const value = durations[index % durations.length] || '';
      const time = /^([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)(ms|s)$/i.exec(value);
      const duration = time ? Number(time[1]) * (time[2].toLowerCase() === 's' ? 1000 : 1) : 0;
      milliseconds = Number.isFinite(duration) ? Math.max(0, duration) : 0;
    });
    return milliseconds;
  }

  goTo(requestedIndex, source = 'api') {
    const toIndex = Math.max(0, Math.min(requestedIndex, this.panels.length - 1));
    const fromIndex = this.index;
    if (toIndex === fromIndex) return false;

    const direction = toIndex > fromIndex ? 1 : -1;
    const detail = this.eventDetail(fromIndex, toIndex, direction, source);
    const beforeChange = new CustomEvent('pageify:beforechange', {
      bubbles: true,
      cancelable: true,
      detail,
    });
    if (!this.root.dispatchEvent(beforeChange)) return false;

    const outgoingPanel = this.panels[fromIndex];
    let transitionDuration = 0;
    const focusedElement = document.activeElement;
    if (outgoingPanel) {
      outgoingPanel.classList.remove('is-active');
      outgoingPanel.classList.add('is-leaving');
      transitionDuration = this.transformTransitionDuration(outgoingPanel);
      const leavingOwner = {};
      this.leavingPanels.set(outgoingPanel, leavingOwner);
      this.schedule(() => {
        if (this.leavingPanels.get(outgoingPanel) !== leavingOwner) return;
        this.leavingPanels.delete(outgoingPanel);
        outgoingPanel.classList.remove('is-leaving');
      }, transitionDuration);
    }

    if (focusedElement instanceof Element && outgoingPanel?.contains(focusedElement)) {
      this.root.focus({ preventScroll: true });
    }
    this.commitState(toIndex, direction);

    this.root.dispatchEvent(new CustomEvent('pageify:change', { bubbles: true, detail }));
    const changeSequence = ++this.sequence;
    this.schedule(() => {
      if (changeSequence !== this.sequence || !this.connected) return;
      this.root.dispatchEvent(new CustomEvent('pageify:afterchange', { bubbles: true, detail }));
    }, transitionDuration);
    return true;
  }

  commitState(index, direction) {
    this.index = index;
    this.root.dataset.pageifyActive = this.panelKeys[index];
    this.root.dataset.pageifyDirection = direction < 0 ? 'previous' : 'next';
    this.root.dataset.turnDirection = direction < 0 ? 'previous' : 'next';

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.style.zIndex = active ? '2' : '1';
      panel.classList.toggle('is-active', active);
      if (active) {
        this.leavingPanels.delete(panel);
        panel.classList.remove('is-leaving');
      }
      panel.toggleAttribute('inert', !active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.syncOffsets(index);
    this.backgrounds.forEach((background, backgroundIndex) => {
      background.classList.toggle('is-active', backgroundIndex === index);
      background.setAttribute('aria-hidden', 'true');
    });

    this.currentCounter.textContent = String(index + 1);
    this.totalCounter.textContent = String(this.panels.length);
    centerOpticalCount(this.currentCounter);
    centerOpticalCount(this.totalCounter);
    if (this.statusCounter) {
      const statusTemplate = this.statusCounter.dataset.pageifyStatusTemplate || 'Chapter __CURRENT__ of __TOTAL__';
      this.statusCounter.textContent = statusTemplate
        .replace('__CURRENT__', String(index + 1))
        .replace('__TOTAL__', String(this.panels.length));
    }
    this.previousButton.disabled = index === 0;
    this.nextButton.disabled = index === this.panels.length - 1;
    this.navigation.dataset.pageifyAtEnd = index === this.panels.length - 1 ? 'true' : 'false';
    this.media.sync();
  }

  trackPositions() {
    const rootRect = this.root.getBoundingClientRect();
    const step = rootRect.height;
    let position = 0;
    let headerGap = 0;

    const positions = this.panels.map((panel, panelIndex) => {
      if (panelIndex > 0) position += step;
      if (panel.dataset.pageifyGapBefore === 'header') {
        const gap = Math.max(0, rootRect.top);
        position += gap;
        headerGap = Math.max(headerGap, gap);
      }
      return position;
    });

    this.root.style.setProperty('--pageify-header-gap', `${headerGap}px`);
    return positions;
  }

  syncOffsets(index = this.index) {
    const positions = this.trackPositions();
    const activePosition = positions[index] ?? 0;

    this.panels.forEach((panel, panelIndex) => {
      panel.style.setProperty('--pageify-offset', `${positions[panelIndex] - activePosition}px`);
    });
    this.backgrounds.forEach((background, backgroundIndex) => {
      background.style.setProperty('--pageify-offset', `${positions[backgroundIndex] - activePosition}px`);
    });
  }

  turn(direction, source) {
    if (this.turnLocked) return false;
    const targetIndex = Math.max(0, Math.min(this.index + direction, this.panels.length - 1));
    if (targetIndex === this.index) return false;
    if (!this.goTo(targetIndex, source)) return false;

    this.turnLocked = true;
    this.schedule(() => {
      this.turnLocked = false;
    }, this.reducedMotion.matches ? 0 : 500);
    return true;
  }

  isBlockedTarget(target) {
    if (!(target instanceof Element) || document.querySelector('dialog[open]')) return true;
    return Boolean(target.closest('form, label, input, textarea, select, option, button, [contenteditable="true"], [data-pageify-gesture-ignore]'));
  }

  onKeydown(event) {
    const navigationControl = event.target instanceof Element
      && event.target.closest('[data-pageify-prev], [data-pageify-next]');
    const pageifyKeyboardTarget = event.target === this.root || navigationControl;
    if (
      event.defaultPrevented
      || event.repeat
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || !pageifyKeyboardTarget
      || document.querySelector('dialog[open]')
    ) return;

    const previousKeys = ['ArrowUp', 'PageUp'];
    const nextKeys = ['ArrowDown', 'PageDown'];
    let targetIndex = null;
    if (previousKeys.includes(event.key)) targetIndex = this.index - 1;
    if (nextKeys.includes(event.key)) targetIndex = this.index + 1;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = this.panels.length - 1;
    if (targetIndex === null) return;

    event.preventDefault();
    if (targetIndex === this.index || this.turnLocked) return;
    if (Math.abs(targetIndex - this.index) === 1) {
      this.turn(targetIndex > this.index ? 1 : -1, 'keyboard');
    } else {
      this.goTo(targetIndex, 'keyboard');
    }
  }

  readingOwner(target) {
    const details = target.closest('.product-details, [data-pageify-reading-region]');
    if (!details || details.closest(ROOT_SELECTOR) !== this.root) return null;
    const mobileSheet = details.closest('[data-monolyt-mobile-sheet]');
    if (mobileSheet && ['auto', 'scroll', 'overlay'].includes(getComputedStyle(mobileSheet).overflowY)) return mobileSheet;
    let owner = target.closest('[data-pageify-scrollable]');
    while (owner && (owner === details || details.contains(owner))) {
      if (['auto', 'scroll', 'overlay'].includes(getComputedStyle(owner).overflowY)) return owner;
      owner = owner.parentElement?.closest('[data-pageify-scrollable]');
    }
    return null;
  }

  onReadingWheel(event, owner, deltaY, now) {
    const previous = this.readingWheel;
    const direction = Math.sign(deltaY);
    const extent = `${owner.scrollHeight}:${owner.clientHeight}`;
    const same = previous?.owner === owner && previous.direction === direction && previous.extent === extent;
    const fresh = !previous || now - previous.lastAt > 420;
    if (!same || fresh) {
      this.readingWheel = { owner, direction, extent, lastAt: now, total: 0, events: 0,
        armed: false, nativeUsed: false, eligible: Boolean(same && fresh && (previous.armed || previous.nativeUsed || previous.eligible)) };
    }
    const state = this.readingWheel;
    state.lastAt = now;
    if (this.findScrollable(event.target, deltaY)) {
      state.nativeUsed = true;
      state.armed = false;
      state.eligible = false;
      state.total = 0;
      state.events = 0;
      return;
    }
    if (event.cancelable) event.preventDefault();
    if (this.turnLocked || now < this.wheelIgnoreUntil || this.wheelGestureBlockedUntilQuiet) return;
    if (!state.eligible) { state.armed = true; return; }
    state.total += Math.abs(deltaY);
    state.events++;
    if (state.total < 240 || state.events < 3) return;
    if (this.turn(direction, 'wheel')) {
      this.wheelLastTurnAt = now;
      // Keep a reading exit protected through the whole visible panel transition.
      this.wheelIgnoreUntil = now + (this.reducedMotion.matches ? 500 : 1000);
      this.wheelGestureBlockedUntilQuiet = true;
      this.readingWheel = null;
    }
  }

  canScroll(element, deltaY) {
    if (!element || element.scrollHeight <= element.clientHeight + 1) return false;
    if (deltaY > 0) return element.scrollTop + element.clientHeight < element.scrollHeight - 1;
    if (deltaY < 0) return element.scrollTop > 1;
    return false;
  }

  findScrollable(target, deltaY) {
    if (!(target instanceof Element)) return null;

    let scrollable = target.closest('[data-pageify-scrollable]');
    while (scrollable && scrollable.closest(ROOT_SELECTOR) === this.root) {
      const overflowY = window.getComputedStyle(scrollable).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && this.canScroll(scrollable, deltaY)) {
        return scrollable;
      }
      scrollable = scrollable.parentElement?.closest('[data-pageify-scrollable]') || null;
    }

    return null;
  }

  onWheel(event) {
    if (event.defaultPrevented || event.ctrlKey || this.isBlockedTarget(event.target)) return;
    const gallery = event.target.closest('slideshow-component[data-monolyt-v22-gallery]');
    if (gallery?.hasAttribute('data-monolyt-wheel-x')) { this.readingWheel = null; return; }
    const readingOwner = this.readingOwner(event.target);
    const { x: deltaX, y: deltaY } = normalizeWheelDeltas(event, gallery?.refs?.scroller || readingOwner || this.root);
    if (Math.abs(deltaY) < 2 || Math.abs(deltaX) >= Math.abs(deltaY)) { this.readingWheel = null; return; }

    const now = window.performance.now();
    const quiet = this.root.querySelector('.product-details, [data-pageify-reading-region]') ? 420 : 180;
    const freshGesture = now - this.wheelLastInputAt > quiet;
    if (freshGesture && now >= this.wheelIgnoreUntil) {
      this.wheelDeltaTotal = 0;
      this.wheelGestureBlockedUntilQuiet = false;
      this.wheelGestureUsedNativeScroll = false;
    }
    this.wheelLastInputAt = now;

    if (readingOwner) return this.onReadingWheel(event, readingOwner, deltaY, now);
    this.readingWheel = null;

    const scrollable = this.findScrollable(event.target, deltaY);
    if (scrollable) {
      this.wheelGestureUsedNativeScroll = true;
      return;
    }

    event.preventDefault();
    if (this.wheelGestureUsedNativeScroll && !freshGesture) return;
    if (this.turnLocked || now < this.wheelIgnoreUntil || this.wheelGestureBlockedUntilQuiet) {
      this.wheelDeltaTotal = 0;
      return;
    }
    if (now - this.wheelLastTurnAt < 1000 && !freshGesture && Math.abs(deltaY) < 46) return;

    this.wheelDeltaTotal += deltaY;
    if (Math.abs(this.wheelDeltaTotal) < 58) return;

    const didTurn = this.turn(this.wheelDeltaTotal > 0 ? 1 : -1, 'wheel');
    this.wheelDeltaTotal = 0;
    if (didTurn) {
      this.wheelLastTurnAt = now;
      this.wheelIgnoreUntil = now + 500;
      this.wheelGestureBlockedUntilQuiet = true;
    }
  }

  onTouchStart(event) {
    if (event.touches.length !== 1 || this.isBlockedTarget(event.target)) {
      this.resetTouch();
      return;
    }

    const touch = event.changedTouches[0];
    this.touch = {
      identifier: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      axis: null,
      target: event.target,
      usedNativeScroll: false,
    };
  }

  findTouch(touchList) {
    if (!this.touch) return null;
    return [...touchList].find((touch) => touch.identifier === this.touch.identifier) || null;
  }

  onTouchMove(event) {
    const touchPoint = this.findTouch(event.touches);
    if (!touchPoint || !this.touch) return;
    this.touch.currentX = touchPoint.clientX;
    this.touch.currentY = touchPoint.clientY;
    const deltaX = this.touch.currentX - this.touch.startX;
    const deltaY = this.touch.currentY - this.touch.startY;

    if (!this.touch.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
      this.touch.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }
    if (this.touch.axis !== 'vertical') return;
    if (this.touch.target.closest('[data-pageify-horizontal]') && Math.abs(deltaX) >= Math.abs(deltaY)) return;

    const scrollable = this.findScrollable(this.touch.target, -deltaY);
    if (scrollable) {
      this.touch.usedNativeScroll = true;
      return;
    }
    event.preventDefault();
  }

  onTouchEnd(event) {
    const touchPoint = this.findTouch(event.changedTouches);
    if (!touchPoint || !this.touch) {
      this.resetTouch();
      return;
    }

    const deltaY = touchPoint.clientY - this.touch.startY;
    const canTurn = this.touch.axis !== 'horizontal'
      && !this.touch.usedNativeScroll
      && Math.abs(deltaY) >= 48
      && !this.turnLocked;
    this.resetTouch();
    if (canTurn) this.turn(deltaY < 0 ? 1 : -1, 'touch');
  }

  resetTouch() {
    this.touch = null;
  }

  hasConstrainedMediaConnection() {
    return hasConstrainedMediaConnection();
  }

  allowsVideoPlayback() {
    return !navigator.connection?.saveData
      && !this.reducedMotion.matches
      && !document.hidden
      && !this.hasConstrainedMediaConnection();
  }

  rebuild() {
    const activeKey = this.panelKeys[this.index];
    const previousBackgrounds = [...this.backgrounds];
    if (!this.portalBackgroundLayers() || !this.buildRegistry()) {
      this.destroy();
      return;
    }
    this.media.release(previousBackgrounds.filter((background) => !this.backgrounds.includes(background)));
    const preservedIndex = this.panelKeys.indexOf(activeKey);
    this.commitState(preservedIndex >= 0 ? preservedIndex : Math.min(this.index, this.panels.length - 1), 0);
    this.setupMobileProductSheet();
  }

  destroy() {
    if (!this.connected) return;
    this.connected = false;
    this.sequence += 1;
    this.abortController.abort();
    this.mobileProductSheetCleanup?.();
    this.backgroundResizeObserver?.disconnect();
    this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
    this.connection?.removeEventListener?.('change', this.onConnectionChange);
    this.timers.forEach((timer) => window.clearTimeout(timer));
    this.timers.clear();

    this.panels.forEach((panel, index) => {
      panel.classList.toggle('is-active', index === 0);
      this.leavingPanels.delete(panel);
      panel.classList.remove('is-leaving');
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('inert');
      panel.style.removeProperty('--pageify-offset');
      panel.style.removeProperty('z-index');
    });
    this.media.release(this.backgrounds);
    this.backgrounds.forEach((background, index) => {
      background.classList.toggle('is-active', index === 0);
      background.removeAttribute('aria-hidden');
      background.style.removeProperty('--pageify-offset');
    });
    this.restoreBackgroundLayers();
    this.root.classList.remove('is-enhanced');
    this.root.style.removeProperty('--pageify-header-gap');
    delete this.root.dataset.pageifyActive;
    delete this.root.dataset.pageifyDirection;
    delete this.root.dataset.turnDirection;
    this.navigation.setAttribute('data-pageify-unready', '');
    this.navigation.setAttribute('aria-hidden', 'true');
    this.previousButton.setAttribute('tabindex', '-1');
    this.nextButton.setAttribute('tabindex', '-1');
    this.previousButton.disabled = true;
    this.nextButton.disabled = true;
    this.navigation.removeAttribute('data-pageify-at-end');
    if (!this.navigationHadAriaControls) this.navigation.removeAttribute('aria-controls');
    instances.delete(this.root);
    liveInstances.delete(this);
    if (!liveInstances.size) document.body.classList.remove('pageify-is-enhanced');
  }
}

function initializeRoot(root) {
  const existing = instances.get(root);
  if (existing) {
    existing.rebuild();
    return existing;
  }

  const instance = new MonolytPageify(root);
  if (!instance.connected) return null;
  instances.set(root, instance);
  liveInstances.add(instance);
  return instance;
}

function rootsWithin(scope) {
  const roots = [];
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
  scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
  return roots;
}

function initializeWithin(scope = document) {
  rootsWithin(scope).forEach((root) => initializeRoot(root));
}

function initializeAffectedRoot(scope) {
  initializeWithin(scope);
  const containingRoot = scope instanceof Element ? scope.closest(ROOT_SELECTOR) : null;
  if (containingRoot && containingRoot !== scope) initializeRoot(containingRoot);
}

initializeWithin();
if (!liveInstances.size) document.documentElement.classList.remove('monolyt-pageify-pending');

document.addEventListener('shopify:section:load', (event) => initializeAffectedRoot(event.target));
document.addEventListener('shopify:section:reorder', (event) => initializeAffectedRoot(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const containingRoot = event.target.closest?.(ROOT_SELECTOR);
  instances.get(containingRoot)?.restoreBackgroundLayers(event.target);
  rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy());
  if (containingRoot && containingRoot !== event.target) {
    queueMicrotask(() => initializeRoot(containingRoot));
  }
});
document.addEventListener('shopify:block:select', (event) => {
  const panel = event.target.closest?.('[data-pageify-panel]');
  const root = panel?.closest(ROOT_SELECTOR);
  const instance = root ? instances.get(root) : null;
  const index = instance?.panels.indexOf(panel) ?? -1;
  if (index >= 0) instance.goTo(index, 'shopify-editor');
});
document.addEventListener('shopify:section:select', (event) => {
  const panel = event.target.closest?.('[data-pageify-panel]')
    || event.target.querySelector?.('[data-pageify-panel]');
  const root = panel?.closest(ROOT_SELECTOR);
  const instance = root ? instances.get(root) : null;
  const index = instance?.panels.indexOf(panel) ?? -1;
  if (index >= 0) instance.goTo(index, 'shopify-editor');
});
